import {useState} from 'react';
import {AxiosError} from "axios";
import {SpecialistListResponse} from "../models/interfaces/specialist-list-response.interface";
import {CreateSpecialistRequest} from "../models/interfaces/create-specialist-request.interface";
import {UpdateSpecialistRequest} from "../models/interfaces/update-specialist-request.interface";
import SpecialistApiService from "../services/specialist-api.service";
import {SpecialistDTO} from "../models/interfaces/specialist-dto.interface";
import {SpecialistProfileImageResponse} from "../models/interfaces/specialist-profile-image-response.interface";
import {useLoading} from "./use-loading.hook";
import {LoadingKeys} from "../models/enums/loading-keys.enum";

export const useSpecialistApi = () => {
    const loadingKey = LoadingKeys.USE_SPECIALIST_API;
    const { startLoading, stopLoading } = useLoading();
    const [error, setError] = useState<string | null>(null);

    const getSearchSpecialists = async (filter: string = "", limit: number = 20, offset: number = 0): Promise<SpecialistListResponse> => {
        startLoading([loadingKey,"getSearchSpecialists"]);
        setError(null);
        try {
            return await SpecialistApiService.getSearchSpecialists(filter, limit, offset);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey,"getSearchSpecialists"]);
        }
    };

    const getSpecialist = async (id: string): Promise<SpecialistDTO> => {
        startLoading([loadingKey,"getSpecialist"]);
        setError(null);
        try {
            return await SpecialistApiService.getSpecialistById(id);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey,"getSpecialist"]);
        }
    };

    const createSpecialist = async (data: CreateSpecialistRequest, image?: File): Promise<SpecialistDTO> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await SpecialistApiService.createSpecialist(data, image);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const updateSpecialist = async (data: UpdateSpecialistRequest): Promise<SpecialistDTO> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await SpecialistApiService.updateSpecialist(data);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const deleteSpecialist = async (id: string): Promise<void> => {
        startLoading(loadingKey);
        setError(null);
        try {
            return await SpecialistApiService.deleteSpecialist(id);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading(loadingKey);
        }
    };

    const getSpecialistProfileImage = async (id: string): Promise<SpecialistProfileImageResponse> => {
        startLoading([loadingKey, "getSpecialistProfileImage"]);
        setError(null);
        try {
            return await SpecialistApiService.getSpecialistProfileImage(id);
        } catch (err: any) {
            const status = (err as AxiosError)?.response?.status;
            if (status === 404) {
                return {base64: ''} as SpecialistProfileImageResponse;
            }
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "getSpecialistProfileImage"]);
        }
    };

    const updateSpecialistProfileImage = async (id: string, image: File): Promise<void> => {
        startLoading([loadingKey, "updateSpecialistProfileImage"]);
        setError(null);
        try {
            return await SpecialistApiService.updateSpecialistProfileImage(id, image);
        } catch (err: any) {
            setError(err.message);
            throw err;
        } finally {
            stopLoading([loadingKey, "updateSpecialistProfileImage"]);
        }
    };

    return {
        error,
        setError,
        getSearchSpecialists,
        getSpecialist,
        createSpecialist,
        updateSpecialist,
        deleteSpecialist,
        getSpecialistProfileImage,
        updateSpecialistProfileImage,
    };
};
